/**
 * The opening view's arithmetic (`retire-edit-mode-opening-view`): the map's camera → the view
 * Position on map saves (R5, R7), and the readout and alt text that say a view in words (R4, R6).
 * Pure functions, so they can be pinned without a map.
 *
 * Distances are Mapbox's own Web Mercator, measured at the twin's origin as `nudgeOrigin` measures
 * them, written out here rather than imported: this module is in the step's module graph, and
 * `mapbox-gl` is not something the step should load just to measure.
 */
import type { CameraSetup } from '@repo/frontend-types';
import type { PlacementOrigin } from './placementGestures';

/** R5: a view's centre stays within this many metres east–west and north–south of the twin. */
export const OPENING_VIEW_RANGE_M = 500;
/** R2: the twin viewer's zoom limits (`MapCanvas`'s `minZoom` and `maxZoom`). */
export const VIEW_MIN_ZOOM = 6;
export const VIEW_MAX_ZOOM = 22;
/** R2: Mapbox's default max pitch, which the viewer also uses. */
export const VIEW_MAX_PITCH = 85;

/** The map's camera as `getCenter`, `getZoom`, `getBearing` and `getPitch` give it. */
export type MapCamera = CameraSetup;

export type OpeningViewResult = { ok: true; view: CameraSetup } | { ok: false; reason: 'too-far' };

const round = (value: number, places: number) => {
  const f = 10 ** places;
  // `+ 0` turns a rounded -0 into 0.
  return Math.round(value * f) / f + 0;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Mapbox's Earth (`earthRadius` 6371008.8 m) and its Web Mercator, x and y in 0–1. */
const EARTH_CIRCUMFERENCE_M = 2 * Math.PI * 6371008.8;
const mercatorX = (lng: number) => (180 + lng) / 360;
const mercatorY = (lat: number) =>
  (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;

/** The view's centre from the origin, in metres east and north. */
export const offsetFromOrigin = (
  at: { lat: number; lng: number },
  origin: PlacementOrigin,
): { east: number; north: number } => {
  // Metres per Mercator unit at the origin: Mapbox's `meterInMercatorCoordinateUnits`, inverted.
  const metres = EARTH_CIRCUMFERENCE_M * Math.cos((origin.latitude * Math.PI) / 180);
  // Mercator y grows southwards.
  return {
    east: (mercatorX(at.lng) - mercatorX(origin.longitude)) * metres,
    north: (mercatorY(origin.latitude) - mercatorY(at.lat)) * metres,
  };
};

/** Whether a centre is inside R5's box about the origin. */
export const withinRange = (at: { lat: number; lng: number }, origin: PlacementOrigin) => {
  const { east, north } = offsetFromOrigin(at, origin);
  return Math.abs(east) <= OPENING_VIEW_RANGE_M && Math.abs(north) <= OPENING_VIEW_RANGE_M;
};

/**
 * R7: the camera as the view saved, every key written: latitude and longitude to 7 places, zoom to 2,
 * bearing (as Mapbox reports it, −180 to 180) and pitch to 1. Zoom and pitch are held to R2's limits.
 * Past R5's box nothing is saved.
 */
export const openingViewFromCamera = (
  camera: MapCamera,
  origin: PlacementOrigin,
): OpeningViewResult => {
  if (!withinRange(camera, origin)) return { ok: false, reason: 'too-far' };
  return {
    ok: true,
    view: {
      lat: round(camera.lat, 7),
      lng: round(camera.lng, 7),
      zoom: round(clamp(camera.zoom, VIEW_MIN_ZOOM, VIEW_MAX_ZOOM), 2),
      bearing: round(camera.bearing, 1),
      pitch: round(clamp(camera.pitch, 0, VIEW_MAX_PITCH), 1),
    },
  };
};

/** A bearing as a compass reads it: 0–359, a whole degree. */
const compass = (bearing: number) => {
  const whole = Math.round(bearing) % 360;
  return whole < 0 ? whole + 360 : whole;
};

/**
 * R4's readout (*Facing 340°, tilted 70°, zoom 19.0, 120 m from the twin*) with an origin; R6's alt
 * text (*Opening view: facing 340°, tilted 70°, zoom 19.0*) without one.
 */
export const describeView = (view: CameraSetup, origin?: PlacementOrigin): string => {
  const angles = `facing ${compass(view.bearing)}°, tilted ${Math.round(view.pitch)}°, zoom ${view.zoom.toFixed(1)}`;
  if (!origin) return `Opening view: ${angles}`;
  const { east, north } = offsetFromOrigin(view, origin);
  const metres = Math.round(Math.hypot(east, north) / 10) * 10;
  return `F${angles.slice(1)}, ${metres} m from the twin`;
};
